<template>
  <div class="scrollable-div">
    <table class="full-width-table">
      <tbody>
        <tr class="table-header">
          <th style="width: 15%">Name</th>
          <th style="width: 12%">Type</th>
          <th style="width: 12%">Location</th>
          <th style="width: 15%">Inside Salesperson</th>
          <th style="width: 15%">Outside Salesperson</th>
          <th style=""></th>
        </tr>
        <tr v-for="contact in contacts">
          <td class="contact-name">
            <span @click="navigate(contact)">{{ contact.name }}</span>
          </td>
          <td>
            <span>{{ contact.type }}</span>
          </td>
          <td>
            <span>{{ contact.location }}</span>
          </td>
          <td class="table-select">
            <select :value="contact.insideSales" v-model="contact.insideSales" @change="contact.insideIsDirty = true" :style="{ fontWeight: isInsideSalesperson(contact.insideSales) ? '' : '600' }">
              <option :value="null"></option>
              <option v-if="!isInsideSalesperson(contact.insideSales) && contact.insideSales" disabled :value="contact.insideSales">{{ getDisplayNameForUserId(contact.insideSales) || 'Unknown User' }}</option>
              <option v-for="person in salespeople" :value="person.USERID">{{ person.DISPLAYNAME }}</option>
            </select>
          </td>
          <td class="table-select">
            <select :value="contact.outsideSales" v-model="contact.outsideSales" @change="contact.outsideIsDirty = true" :style="{ fontWeight: isOutsideSalesperson(contact.outsideSales) ? '' : '600' }">
              <option :value="null"></option>
              <option v-if="!isOutsideSalesperson(contact.outsideSales) && contact.outsideSales" disabled :value="contact.outsideSales">{{ getDisplayNameForUserId(contact.outsideSales) || 'Unknown User' }}</option>
              <option v-for="person in outsideSalespeople" :value="person.USERID">{{ person.DISPLAYNAME }}</option>
            </select>
          </td>
          <td></td>
        </tr>
        <tr v-if="loading" class="loading-row">
          <td colspan="6"><img src="../../assets/spinner.svg" /></td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script>
import useDecryptSymbols from '@/composables/useDecryptSymbols';
import useUnassignedSalespeople from '@/composables/useUnassignedSalespeopleQuery';
import useUsersQuery from '@/composables/useUsersQuery';
import useCompanyMutate from '@/composables/useCompanyMutate';
import useContactMutate from '@/composables/useContactMutate';
import useAlerts from '@/composables/useAlerts';
import useReminderMutate from '@/composables/useReminderMutate';
import useCurrentUser from '@/composables/useCurrentUser';
import { loadRouteLocation } from 'vue-router';

export default {
  name: 'AssignSalespersonContacts',
  emits: ['isDirty', 'isSaving'],
  setup() {
    const { decryptSymbols, encryptSymbols } = useDecryptSymbols();
    const { result, loading, error, refetch } = useUnassignedSalespeople();
    const { updateCompany } = useCompanyMutate();
    const { updateContact } = useContactMutate();
    const { addAlert } = useAlerts();
    const { addReminder } = useReminderMutate();
    const { user } = useCurrentUser();
    const { salespeople, outsideSalespeople } = useUsersQuery();

    return {
      decryptSymbols,
      encryptSymbols,
      result,
      loading,
      error,
      refetch,
      salespeople,
      outsideSalespeople,
      updateCompany,
      updateContact,
      addAlert,
      addReminder,
      user,
    };
  },
  data() {
    return {
      contacts: [],
    };
  },
  watch: {
    result() {
      this.loadContacts();
    },
    loading() {
      this.loadContacts();
    },
    contacts: {
      handler() {
        this.$emit(
          'isDirty',
          this.contacts.some((c) => c.insideIsDirty || c.outsideIsDirty)
        );
      },
      deep: true,
    },
  },
  methods: {
    loadContacts() {
      if (!this.result?.unassignedSalespeople || this.loading) {
        this.contacts = [];
        return;
      }
      const resultingContacts = [];
      const decrypted = this.decryptSymbols(this.result.unassignedSalespeople);
      decrypted.companies.forEach((company) => {
        let address = company.address?.CITY;
        if (company.address?.STATE) {
          if (address) address += ', ';
          address += company.address?.STATE;
        }

        const phone = company.phones.find((phone) => phone.TYPEID === 'C3AE9586-6158-4D0D-9FAF-253CB0152F18')?.NUMBERDISPLAY || '';

        resultingContacts.push({
          type: 'Company',
          id: company.COMPANYID,
          name: company.NAME,
          insideSales: company.INSIDESALES,
          outsideSales: company.OUTSIDESALES,
          location: address,
          phone: this.formatPhone(phone),
          insideIsDirty: false,
          outsideIsDirty: false,
        });
      });

      decrypted.contacts.forEach((contact) => {
        let address = contact.address?.CITY;
        if (contact.address?.STATE) {
          if (address) address += ', ';
          address += contact.address?.STATE;
        }

        const phone = contact.phones.find((phone) => phone.TYPEID === '511DF006-2CDB-40B5-8D1A-B313C873DD1C')?.NUMBERDISPLAY || '';

        resultingContacts.push({
          type: 'Contact',
          id: contact.CONTACTID,
          name: `${contact.FIRSTNAME} ${contact.LASTNAME}`,
          firstName: contact.FIRSTNAME,
          lastName: contact.LASTNAME,
          insideSales: contact.INSIDESALES,
          outsideSales: contact.OUTSIDESALES,
          location: address,
          phone: this.formatPhone(phone),
          insideIsDirty: false,
          outsideIsDirty: false,
        });
      });

      this.contacts = resultingContacts;
    },
    navigate(contact) {
      if (contact.type == 'Company') {
        this.$router.push({ name: 'companies', params: { companyid: contact.id } });
      } else if (contact.type == 'Contact') {
        this.$router.push({ name: 'contacts', params: { contactid: contact.id } });
      }
    },
    async save() {
      this.$emit('isSaving', true);
      const updatePromises = [];
      const reminderPromises = [];

      for (const contact of this.contacts.filter((c) => c.insideIsDirty || c.outsideIsDirty)) {
        if (contact.type === 'Company') {
          updatePromises.push(
            this.updateCompany(contact.id, {
              INSIDESALES: contact.insideSales,
              OUTSIDESALES: contact.outsideSales,
            })
          );
        } else if (contact.type === 'Contact') {
          updatePromises.push(
            this.updateContact(contact.id, {
              INSIDESALES: contact.insideSales,
              OUTSIDESALES: contact.outsideSales,
            })
          );
        }

        if (contact.insideIsDirty && contact.insideSales) {
          reminderPromises.push(
            this.addReminder({
              TYPE: `Assigned Customer`,
              ASSIGNEDID: contact.insideSales,
              CONTACTID: contact.type === 'Company' ? null : contact.id,
              COMPANYID: contact.type === 'Company' ? contact.id : null,
              COMPLETED: false,
              TEXT: `You are now the assigned inside salesperson for ${this.encryptSymbols(contact.name || contact.firstName + ' ' + contact.lastName)}.`,
              DATE: new Date().toISOString(),
            })
          );
        }

        if (contact.outsideIsDirty && contact.outsideSales) {
          reminderPromises.push(
            this.addReminder({
              TYPE: `Assigned Customer`,
              ASSIGNEDID: contact.outsideSales,
              CONTACTID: contact.type === 'Company' ? null : contact.id,
              COMPANYID: contact.type === 'Company' ? contact.id : null,
              COMPLETED: false,
              TEXT: `You are now the assigned outside salesperson for ${this.encryptSymbols(contact.name || contact.firstName + ' ' + contact.lastName)}.`,
              DATE: new Date().toISOString(),
            })
          );
        }
      }

      try {
        await Promise.all(updatePromises);
        await Promise.all(reminderPromises);

        this.addAlert('Updated salespeople', 'low', 3);
        this.$emit('isDirty', false);
      } catch (error) {
        console.log(error);
      }

      this.$emit('isSaving', false);
    },
    formatPhone(phone) {
      return phone.replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3');
    },
    isInsideSalesperson(salespersonId) {
      return this.salespeople.some((person) => person.USERID === salespersonId);
    },
    isOutsideSalesperson(salespersonId) {
      return this.outsideSalespeople.some((person) => person.USERID === salespersonId);
    },
    getDisplayNameForUserId(userId) {
      if (!userId) return '';
      const allUsers = [...this.salespeople, ...this.outsideSalespeople];
      const user = allUsers.find((person) => person.USERID === userId);
      return user ? user.DISPLAYNAME : '';
    },
  },
  mounted() {
    this.refetch();
  },
};
</script>

<style lang="scss" scoped>
.contact-name {
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
}
</style>
